import { useState, useEffect, useCallback } from 'react';
import { SEARCH_MIN_LENGTH } from '../../../../constants/person-constants';
import { searchPersons } from '../services/persons-manager.service';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { useDebouncedValue } from './useDebouncedValue';
import { usePersonResultAttributes } from './usePersonResultAttributes';
import { usePersonsManagerStyles } from '../persons-manager.style';

/**
 * @typedef {Object} SearchPersonRow
 * @property {number} personId
 * @property {string} [username]
 * @property {string} [email]
 * @property {string} [docId]
 */

/**
 * @typedef {Object} ToastState
 * @property {'error'|'success'|'info'} type
 * @property {string} message
 */

export const usePersonsManagerHook = () => {
  const classes = usePersonsManagerStyles();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 500);
  const [muiPage, setMuiPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState({
    /** @type {SearchPersonRow[]} */
    results: [],
    totalCount: 0,
    totalPages: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchError, setSearchError] = useState(null);

  const { results, totalCount } = searchResult;
  const { attributesByPersonId, refreshPersonAttributes } = usePersonResultAttributes(results);

  const [modalPerson, setModalPerson] = useState(/** @type {SearchPersonRow | null} */ (null));

  const [toast, setToast] = useState(/** @type {ToastState | null} */ (null));

  useEffect(() => {
    setMuiPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (q.length < SEARCH_MIN_LENGTH) {
      setSearchResult({
        results: [],
        totalCount: 0,
        totalPages: 0,
        page: 1,
        pageSize: rowsPerPage,
      });
      setSearchError(null);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const data = await searchPersons({
          q,
          page: muiPage + 1,
          pageSize: rowsPerPage,
        });
        if (cancelled) return;
        setSearchResult({
          results: data.results || [],
          totalCount: data.totalCount ?? 0,
          totalPages: data.totalPages ?? 0,
          page: data.page ?? muiPage + 1,
          pageSize: data.pageSize ?? rowsPerPage,
        });
      } catch (e) {
        if (!cancelled) {
          setSearchError(e);
          setToast({ type: 'error', message: 'Search failed. Check console or try again.' });
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, muiPage, rowsPerPage]);

  const handleModalSaved = useCallback(() => {
    if (modalPerson?.personId != null) {
      refreshPersonAttributes(modalPerson.personId);
    }
  }, [modalPerson, refreshPersonAttributes]);

  /** Ensures React state matches the field after paste (some browsers order events differently). */
  const handleSearchPaste = useCallback((e) => {
    const el = e.target;
    window.setTimeout(() => {
      if (el && typeof el.value === 'string') {
        setSearchInput(el.value);
      }
    }, 0);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.currentTarget.value);
  }, []);

  const handlePageChange = useCallback((_, page) => {
    setMuiPage(page);
  }, []);

  const handleRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number.parseInt(e.target.value, 10));
    setMuiPage(0);
  }, []);

  const handleCloseToast = useCallback(() => {
    setToast(null);
  }, []);

  const showHint =
    debouncedSearch.trim().length > 0 && debouncedSearch.trim().length < SEARCH_MIN_LENGTH;

  return {
    classes,
    searchInput,
    handleSearchChange,
    handleSearchPaste,
    debouncedSearch,
    showHint,
    searchLoading,
    searchError,
    results,
    totalCount,
    muiPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    attributesByPersonId,
    modalPerson,
    setModalPerson,
    handleModalSaved,
    toast,
    handleCloseToast,
  };
};
